import type { FC } from "react";
import { Controller, Control, useWatch } from "react-hook-form";
import { Box, TextField, FormControlLabel, Checkbox, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { JsonAttribute, JsonAttributeType } from "src/api/client-api/jsonAttributes/useJsonAttribute";

interface JsonAttributesFieldProps {
    /** RHF Control is invariant; callers may need `as Control<any>` when passing a typed control. */
    control: Control<any>;
    jsonAttributes: JsonAttribute[];
}

export const JsonAttributesField: FC<JsonAttributesFieldProps> = ({ control, jsonAttributes }) => {
    const { t } = useTranslation();

    const watchedJsonAttributes = useWatch({
        control,
        name: "jsonAttributes",
        defaultValue: {},
    });

    if (!jsonAttributes || jsonAttributes.length === 0) {
        return null;
    }

    // Styles communs pour tous les champs TextField (identiques à ceux du UserModal)
    const commonTextFieldStyles = {
        "& .MuiOutlinedInput-root": {
            borderRadius: "24px",
            minHeight: "40px",
            backgroundColor: "white",
            "& fieldset": {
                borderColor: "#E7E6E4",
            },
            "&:hover fieldset": {
                borderColor: "#D0D0D0",
            },
            "&.Mui-focused fieldset": {
                borderColor: "primary.main",
            },
        },
        "& .MuiInputBase-input": {
            fontSize: "0.875rem",
            padding: "0px 16px !important",
            color: "#393A36",
            "&::placeholder": {
                color: "#A3A2A0",
                opacity: 1,
            },
        },
    };

    // Styles pour les labels (identiques à ceux du UserModal)
    const labelStyles = {
        fontWeight: 700,
        fontSize: "0.875rem",
        lineHeight: "1.6em",
        color: "#393A36",
        marginBottom: "8px",
    };

    const renderField = (jsonAttribute: JsonAttribute) => {
        const fieldName = `jsonAttributes.${jsonAttribute.identifier}`;

        switch (jsonAttribute.type) {
            case JsonAttributeType.STRING:
                return (
                    <Box key={jsonAttribute.identifier} sx={{ marginBottom: "24px" }}>
                        <Typography sx={labelStyles}>{jsonAttribute.label}:</Typography>
                        <Controller
                            name={fieldName}
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    placeholder={`${t("jsonAttributes.enter", "Enter")} ${jsonAttribute.label.toLowerCase()}`}
                                    sx={commonTextFieldStyles}
                                />
                            )}
                        />
                    </Box>
                );

            case JsonAttributeType.INTEGER:
            case JsonAttributeType.FLOAT:
                return (
                    <Box key={jsonAttribute.identifier} sx={{ marginBottom: "24px" }}>
                        <Typography sx={labelStyles}>{jsonAttribute.label}:</Typography>
                        <Controller
                            name={fieldName}
                            control={control}
                            defaultValue=""
                            render={({ field: { value, onChange, ...field } }) => (
                                <TextField
                                    {...field}
                                    type="number"
                                    fullWidth
                                    value={value}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "") {
                                            onChange("");
                                        } else {
                                            const numVal =
                                                jsonAttribute.type === JsonAttributeType.INTEGER ?
                                                    parseInt(val, 10)
                                                :   parseFloat(val);
                                            if (!isNaN(numVal)) {
                                                onChange(numVal);
                                            }
                                        }
                                    }}
                                    placeholder={`${t("jsonAttributes.enter", "Enter")} ${jsonAttribute.label.toLowerCase()}`}
                                    sx={commonTextFieldStyles}
                                />
                            )}
                        />
                    </Box>
                );

            case JsonAttributeType.BOOLEAN:
                return (
                    <Box key={jsonAttribute.identifier} sx={{ marginBottom: "24px" }}>
                        <Controller
                            name={fieldName}
                            control={control}
                            defaultValue={false}
                            render={({ field: { value, onChange } }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={Boolean(value)}
                                            onChange={(e) => onChange(e.target.checked)}
                                            sx={{
                                                color: "#767673",
                                                "&.Mui-checked": {
                                                    color: "primary.main",
                                                },
                                            }}
                                        />
                                    }
                                    label={<Typography sx={labelStyles}>{jsonAttribute.label}</Typography>}
                                />
                            )}
                        />
                    </Box>
                );

            default:
                return (
                    <Box key={jsonAttribute.identifier} sx={{ marginBottom: "24px" }}>
                        <Typography sx={labelStyles}>{jsonAttribute.label}:</Typography>
                        <Controller
                            name={fieldName}
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    placeholder={`${t("jsonAttributes.enter", "Enter")} ${jsonAttribute.label.toLowerCase()}`}
                                    sx={commonTextFieldStyles}
                                />
                            )}
                        />
                    </Box>
                );
        }
    };

    return <Box>{jsonAttributes.map((jsonAttribute) => renderField(jsonAttribute))}</Box>;
};
